import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, ArrowUp, Star, CheckCircle, Zap } from 'lucide-react';

export default function UpgradePage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Feature Locked
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            This feature is available in our premium plans. Upgrade your plan to unlock powerful tools and advanced functionality.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {/* Advanced Analytics */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Analytics</h3>
            <p className="text-gray-600 text-sm">
              Get detailed insights into your group's performance with comprehensive reports and analytics.
            </p>
          </div>

          {/* Bulk SMS */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
              <Star className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Bulk SMS</h3>
            <p className="text-gray-600 text-sm">
              Send messages to all members instantly with our bulk SMS feature.
            </p>
          </div>

          {/* Document Management */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Document Management</h3>
            <p className="text-gray-600 text-sm">
              Store and manage important group documents securely in the cloud.
            </p>
          </div>

          {/* Advanced Loans */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
              <ArrowUp className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Loans</h3>
            <p className="text-gray-600 text-sm">
              Manage complex loan types, repayment schedules, and loan analytics.
            </p>
          </div>

          {/* Welfare Plans */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4">
              <Star className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Welfare Plans</h3>
            <p className="text-gray-600 text-sm">
              Create and manage welfare plans for your group members.
            </p>
          </div>

          {/* Meeting Management */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Meeting Management</h3>
            <p className="text-gray-600 text-sm">
              Schedule meetings, track attendance, and manage meeting notes.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Upgrade?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Unlock all premium features and take your group management to the next level. 
            Choose a plan that fits your needs and start enjoying advanced functionality today.
          </p>
          <div className="flex flex-col gap-4 justify-center items-center">
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-lg">
              <button
                onClick={() => navigate('/?upgrade=true')}
                className="flex-1 inline-flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold shadow-md hover:from-blue-700 hover:to-indigo-700 transition-all text-lg"
              >
                Upgrade Automatically via M-Pesa
              </button>
            </div>
            
            <div className="text-gray-500 font-medium">OR</div>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-lg">
              <div className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-gray-50 border border-gray-200 text-gray-700 rounded-lg text-sm text-center">
                Manual Upgrade: Make a payment via Paybill 4167393. Use your group name as the account number.
              </div>
            </div>
            
            <div className="mt-4">
              <a
                href="https://twachanga.co.ke/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                View Current Plan Details →
              </a>
            </div>
          </div>
        </div>

        {/* Back to Dashboard */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
} 